import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { adminApi, checkImageFile, errorMessage, imgSrc } from "@/lib/api";

export const ImageManager = ({ product, onChange }) => {
  const input = useRef(null);
  const [uploads, setUploads] = useState([]);
  const images = product.images || [];

  const upload = async (files) => {
    for (const file of files) {
      const bad = checkImageFile(file);
      if (bad) { toast.error(`${file.name}: ${bad}`); continue; }
      const key = `${file.name}-${Date.now()}`;
      const preview = URL.createObjectURL(file);
      setUploads((u) => [...u, { key, preview, progress: 0 }]);
      try {
        const updated = await adminApi.uploadImage(product.id, file, (pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: pct } : x))));
        onChange(updated);
        toast.success("Photo uploaded");
      } catch (e) {
        toast.error(`Upload failed: ${errorMessage(e)}`);
      } finally {
        URL.revokeObjectURL(preview);
        setUploads((u) => u.filter((x) => x.key !== key));
      }
    }
  };

  const run = async (fn, msg) => {
    try { onChange(await fn()); toast.success(msg); } catch (e) { toast.error(errorMessage(e)); }
  };
  const move = (idx, dir) => {
    const ids = images.map((i) => i.id);
    [ids[idx], ids[idx + dir]] = [ids[idx + dir], ids[idx]];
    run(() => adminApi.reorderImages(product.id, ids), "Photo order saved");
  };

  return (
    <div data-testid="image-manager">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {images.map((im, idx) => (
          <div key={im.id} className="group relative aspect-square overflow-hidden rounded-xl border border-[#E5E0D8] bg-[#F4EEE2]" data-testid={`admin-image-${idx}`}>
            <img src={imgSrc(im.url)} alt="" className="h-full w-full object-cover" />
            {idx === 0 && <span className="absolute left-1.5 top-1.5 rounded bg-[#1B4332] px-1.5 text-[10px] font-bold text-white">COVER</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 p-1">
              <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="grid h-8 w-8 place-items-center rounded text-white disabled:opacity-30" aria-label="Move left" data-testid={`image-move-left-${idx}`}><ArrowLeft className="h-4 w-4" /></button>
              <button type="button" onClick={() => run(() => adminApi.deleteImage(product.id, im.id), "Photo removed")} className="grid h-8 w-8 place-items-center rounded text-white" aria-label="Remove photo" data-testid={`image-delete-${idx}`}><Trash2 className="h-4 w-4" /></button>
              <button type="button" disabled={idx === images.length - 1} onClick={() => move(idx, 1)} className="grid h-8 w-8 place-items-center rounded text-white disabled:opacity-30" aria-label="Move right" data-testid={`image-move-right-${idx}`}><ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {uploads.map((u) => (
          <div key={u.key} className="relative aspect-square overflow-hidden rounded-xl border border-[#C59B27]/50">
            <img src={u.preview} alt="" className="h-full w-full object-cover opacity-60" />
            <div className="absolute inset-x-2 bottom-2"><Progress value={u.progress} className="h-1.5" /></div>
          </div>
        ))}
        {images.length + uploads.length < 10 && (
          <button type="button" onClick={() => input.current?.click()} className="grid aspect-square place-items-center rounded-xl border-2 border-dashed border-[#C59B27]/60 text-[#8B5A2B] hover:bg-[#FBF3DC]" data-testid="image-upload-btn">
            <span className="flex flex-col items-center gap-1 text-xs font-semibold"><Upload className="h-5 w-5" /> Add photos</span>
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="image-upload-input" />
      <p className="mt-2 text-xs text-[#5B625D]">JPEG, PNG or WebP · up to 6 MB each · first photo is the cover. Photos are optimised automatically.</p>
    </div>
  );
};
